import { supabase } from '@/lib/supabase'

const BUCKET = 'invoices'
export const MAX_INVOICE_FILE_SIZE = 10 * 1024 * 1024 // 10MB（migration-034 のバケット上限と一致）

/**
 * 請求書 PDF を invoices バケットにアップロードし、保存パス（invoices.file_url に格納する値）を返す。
 * 既存データと同じく バケット直下に {timestamp}.pdf 形式で保存する。
 * 失敗時は throw（呼び出し側で toast 表示する想定）。
 */
export async function uploadInvoiceFile(file: File): Promise<string> {
  if (file.type !== 'application/pdf') {
    throw new Error('PDFファイルのみアップロードできます')
  }
  if (file.size > MAX_INVOICE_FILE_SIZE) {
    throw new Error(`ファイルが大きすぎます (${Math.round(file.size / 1024 / 1024)}MB). 10MB以下にしてください`)
  }
  const path = `${Date.now()}.pdf`
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: 'application/pdf',
  })
  if (error) throw new Error(`アップロードに失敗: ${error.message}`)
  return path
}

/**
 * 署名付きURLのセッション内キャッシュ（非公開バケットのため毎回発行を避ける）。
 */
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>()

/** 非公開バケットの署名付きURLを返す（閲覧用）。 */
export async function getInvoiceFileUrl(path: string, expiresInSec = 3600): Promise<string | null> {
  const cached = signedUrlCache.get(path)
  if (cached && cached.expiresAt > Date.now() + 60_000) return cached.url

  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresInSec)
  if (error || !data) {
    console.error('[invoiceFiles] signed url failed', error)
    return null
  }
  signedUrlCache.set(path, { url: data.signedUrl, expiresAt: Date.now() + expiresInSec * 1000 })
  return data.signedUrl
}

/** ストレージからファイルを削除。 */
export async function deleteInvoiceFile(path: string): Promise<boolean> {
  const { error } = await supabase.storage.from(BUCKET).remove([path])
  if (error) {
    console.error('[invoiceFiles] delete failed', error)
    return false
  }
  signedUrlCache.delete(path)
  return true
}
